/**
 * Health Categories Utility for Patient Dashboard
 * Maps quantitative test names to health categories for biomarker grouping
 */

export interface HealthCategory {
    name: string;
    tests: string[];
}

export const HEALTH_CATEGORIES: Record<string, HealthCategory> = {
    heart: {
        name: 'Heart Health',
        tests: [
            'APO B',
            'Cholesterol',
            'Cholesterol-C',
            'HDL',
            'HDL-C',
            'Direct HDL',
            'LDL',
            'LDLX-C',
            'VLDL-C',
            'Triglycerides',
            'Triglycerides-C',
            'Total Cholesterol',
            'Cholesterol/HDL Ratio',
            'Lipoprotein(a)',
            'CK',
            'CK-MB',
            'CRP',
            'hs-CRP',
            'high sensitivity CRP',
            'Homocysteine',
        ],
    },

    metabolic: {
        name: 'Metabolic Risk',
        tests: [
            'Glucose',
            'HbA1c',
            'Insulin',
            // CMP (Comprehensive Metabolic Panel) components
            'Enzymatic CO2',
            'Carbon Dioxide (CO₂/Biocarbonate)',
            'Calcium',
            'Sodium',
            'Potassium',
            'Chloride',
            'Magnesium',
            'Phosphorus',
            'Uric Acid',
        ],
    },

    'liver-kidney': {
        name: 'Liver & Kidney',
        tests: [
            'ALT',
            'AST',
            'ALP',
            'ALKP',
            'GGT',
            'Amylase',
            'BUN',
            'BUN/Creat Ratio',
            'Creatinine',
            'Total Bilirubin',
            'Direct Bilirubin',
            'Albumin',
            'Total Protein',
        ],
    },

    micronutrient: {
        name: 'Vitamins & Nutrients',
        tests: ['Ferritin', 'Folate', 'Iron', 'TIBC', 'Vitamin B12', 'Vitamin D (OH-25)'],
    },

    hormone: {
        name: 'Hormone',
        tests: [
            'Cortisol',
            'DHEA-S',
            'Estradiol',
            'Free Androgen Index',
            'Free Testosterone',
            'FSH',
            'IGF-1',
            'LH',
            'Progesterone',
            'Prolactin',
            'SHBG',
            'Testosterone',
            'Total Testosterone',
            'Bioavailable Testosterone',
            'TSH',
            'Free T3',
            'Free T4',
            'T3, Free',
            'T4, Free',
            'Total T3',
            'Total T4',
            'T3 Uptake',
            "Men's Hormone Health",
            "Woman's Hormone Health",
        ],
    },

    other: {
        name: 'Other',
        tests: [
            // From Blood Health (excluding micronutrients moved above)
            '01; WBC',
            '02; RBC',
            '03; HGB',
            '04; HCT',
            '05; MCV',
            '06; MCH',
            '07; MCHC',
            '08; PLT',
            '09; RDW',
            '10; MPV',
            '11; NEUT%',
            '12; LYMP%',
            '13; MONO%',
            '14; EOS%',
            '15; BASO%',
            '16; NEUT#',
            '17; LYMP#',
            '18; MONO#',
            '19; EOS#',
            '20; BASO#',
            // From Digestive Health
            'Lipase',
            // From Men's Health
            'PSA, Total',
            // From Immune & Allergy
            '01; Total IgE',
            'Almond',
            'Cashew',
            'Cat Dander',
            'Dog Dander',
            'Egg White',
            'Hazelnut',
            'Peanut',
            'Salmon',
            'Scallop',
            'Sesame',
            'Shrimp',
            'Soybean',
            'Tuna',
            'Walnut',
            'Wheat',
            "Cow's Milk",
            'Cod Fish',
            'Ana o 3',
            'Ber e 1',
            'Cor a 1',
            'Cor a 14',
            'Cor a 8',
            'Cor a 9',
            'Jug r 1',
            'Jug r 3',
        ],
    },
};

// Reverse mapping for quick lookup
export const TEST_TO_CATEGORY: Record<string, string> = {};
Object.entries(HEALTH_CATEGORIES).forEach(([categoryKey, category]) => {
    category.tests.forEach((testName) => {
        TEST_TO_CATEGORY[testName] = categoryKey;
    });
});

/**
 * Get the category key for a given test name
 * If test is not found in any category, it returns 'other'
 */
export const getTestCategory = (testName: string): string => {
    return TEST_TO_CATEGORY[testName] || 'other';
};

/**
 * Get the full category object for a given test name
 * If test is not found in any category, returns the 'other' category
 */
export const getTestCategoryInfo = (testName: string): HealthCategory => {
    const categoryKey = getTestCategory(testName);
    return HEALTH_CATEGORIES[categoryKey];
};

/**
 * Get all unmapped test names from a list of results
 * These are tests that don't exist in any predefined category
 */
export const getUnmappedTests = (results: Array<{test_name: string; [key: string]: any}>): string[] => {
    return results.filter((result) => !TEST_TO_CATEGORY[result.test_name]).map((result) => result.test_name);
};

/**
 * Filter test results by category
 */
export const filterTestsByCategory = (
    results: Array<{test_name: string; result: string; [key: string]: any}>,
    categoryKey: string
): Array<{test_name: string; result: string; [key: string]: any}> => {
    const category = HEALTH_CATEGORIES[categoryKey];
    if (!category) return [];

    return results.filter((result) => category.tests.includes(result.test_name));
};

/**
 * Group test results by health categories
 * Automatically places unmapped tests into the 'other' category
 */
export const groupTestsByCategory = (
    results: Array<{test_name: string; result: string; [key: string]: any}>
): Record<string, Array<{test_name: string; result: string; [key: string]: any}>> => {
    const grouped: Record<string, Array<{test_name: string; result: string; [key: string]: any}>> = {};

    // Initialize all categories with empty arrays
    Object.keys(HEALTH_CATEGORIES).forEach((categoryKey) => {
        grouped[categoryKey] = [];
    });

    // Group each test result into its appropriate category
    results.forEach((result) => {
        const categoryKey = getTestCategory(result.test_name);
        grouped[categoryKey].push(result);
    });

    return grouped;
};

/**
 * Parse a numeric value from a result string
 */
const parseNumericValue = (value: string): number | null => {
    if (!value) return null;

    // Remove common non-numeric characters and extract number
    const cleaned = value.replace(/[<>≤≥]/g, '').trim();
    const match = cleaned.match(/[\d.]+/);

    if (match) {
        const parsed = parseFloat(match[0]);
        return isNaN(parsed) ? null : parsed;
    }

    return null;
};

/**
 * Parse reference range string (e.g., "30-100", "< 5.7", "2.5 - 4.5")
 */
const parseReferenceRange = (refRange: string): {min: number; max: number} | null => {
    if (!refRange) return null;

    // Handle different reference range formats
    const cleaned = refRange.replace(/[≤≥]/g, '').trim();

    // Format: "min - max" or "min-max"
    const rangeMatch = cleaned.match(/(\d+\.?\d*)\s*-\s*(\d+\.?\d*)/);
    if (rangeMatch) {
        const min = parseFloat(rangeMatch[1]);
        const max = parseFloat(rangeMatch[2]);
        if (!isNaN(min) && !isNaN(max)) {
            return {min, max};
        }
    }

    // Format: "< value" (upper limit only)
    const upperMatch = cleaned.match(/[<]\s*(\d+\.?\d*)/);
    if (upperMatch) {
        const max = parseFloat(upperMatch[1]);
        if (!isNaN(max)) {
            return {min: 0, max};
        }
    }

    // Format: "> value" (lower limit only)
    const lowerMatch = cleaned.match(/[>]\s*(\d+\.?\d*)/);
    if (lowerMatch) {
        const min = parseFloat(lowerMatch[1]);
        if (!isNaN(min)) {
            return {min, max: min * 2}; // Approximate upper bound
        }
    }

    return null;
};

/**
 * Normalize a test value based on its reference range (0-100 scale)
 * 50 = middle of normal range, <50 = below normal, >50 = above normal
 */
const normalizeTestValue = (result: {result: string; refRange?: string}): number | null => {
    const numericValue = parseNumericValue(result.result);
    if (numericValue === null) return null;

    // If no reference range, return the raw value (fallback)
    if (!result.refRange) return numericValue;

    const range = parseReferenceRange(result.refRange);
    if (!range) return numericValue;

    // Calculate position within the range (0-100%)
    const position = ((numericValue - range.min) / (range.max - range.min)) * 100;

    // Return normalized score (0-100, can go beyond for values outside range)
    return Math.max(0, position);
};

/**
 * Get summary statistics for each category
 */
export const getCategoryStats = (
    results: Array<{test_name: string; result: string; refRange?: string; [key: string]: any}>
) => {
    const grouped = groupTestsByCategory(results);

    return Object.entries(HEALTH_CATEGORIES).map(([categoryKey, category]) => {
        const categoryResults = grouped[categoryKey];

        // Calculate average normalized value for the category (based on reference ranges)
        const normalizedValues = categoryResults
            .map((result) => normalizeTestValue(result))
            .filter((value): value is number => value !== null);

        const averageValue =
            normalizedValues.length > 0
                ? normalizedValues.reduce((sum, val) => sum + val, 0) / normalizedValues.length
                : 0;

        return {
            key: categoryKey,
            name: category.name,
            totalTests: category.tests.length,
            availableTests: categoryResults.length,
            averageValue: Math.round(averageValue), // Round to whole number for display
            tests: categoryResults,
        };
    });
};

export default HEALTH_CATEGORIES;
